/**
 * Ping handler — pipeline health check with formatted output.
 */

import { runInAction } from 'mobx';
import { fetchPing } from '@api/chat';
import type { PingResponse } from '@api/chat';
import type { ChatMessage } from './types';

export async function pingPipeline(
  messages: ChatMessage[],
  setLoading: (v: boolean) => void,
  persistLocal: () => void,
  nextId: () => string,
): Promise<void> {
  const userMsg: ChatMessage = {
    id: nextId(),
    role: 'user',
    content: '/ping',
    timestamp: new Date().toISOString(),
  };
  messages.push(userMsg);

  const loadingId = nextId();
  messages.push({
    id: loadingId,
    role: 'assistant',
    content: '',
    timestamp: new Date().toISOString(),
    isLoading: true,
  });
  setLoading(true);

  try {
    const data = await fetchPing();
    runInAction(() => {
      const idx = messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        messages[idx] = {
          id: loadingId,
          role: 'assistant',
          content: formatPingResult(data),
          timestamp: new Date().toISOString(),
          isMarkdown: true,
        };
      }
      setLoading(false);
      persistLocal();
    });
  } catch {
    runInAction(() => {
      const idx = messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        messages[idx] = {
          id: loadingId,
          role: 'assistant',
          content: 'Unable to reach the server. Pipeline may be offline.',
          timestamp: new Date().toISOString(),
          isError: true,
        };
      }
      setLoading(false);
      persistLocal();
    });
  }
}

export function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function formatPingResult(data: PingResponse): string {
  const status = data.status.toUpperCase();
  const check = (ok: boolean) => ok ? 'healthy' : 'unreachable';
  const hw = data.hardware;

  const models = data.ollama.models.length
    ? data.ollama.models.map((m) => m.split(':')[0]).join(', ')
    : 'none';
  const missing = data.ollama.missingModels.length
    ? data.ollama.missingModels.join(', ')
    : 'none';
  const cols = data.chromadb.collections.length
    ? `${data.chromadb.collections.join(', ')} (${data.chromadb.collections.length})`
    : 'none';

  const lines = [
    `**Pipeline Status: ${status}**`,
    '',
    `**Ollama** — ${check(data.ollama.healthy)}`,
    `  Models: ${models}`,
    `  Missing: ${missing}`,
    '',
    `**ChromaDB** — ${check(data.chromadb.healthy)}`,
    `  Collections: ${cols}`,
    '',
    `**Model** — ${data.model?.status ?? 'unknown'}${data.model?.warmupDurationMs != null ? ` (warmed up in ${formatUptime(Math.round(data.model.warmupDurationMs / 1000))})` : ''}`,
    '',
    '**Hardware**',
    `  Host: ${hw.hostname}`,
  ];

  if (hw.cpuModel) lines.push(`  CPU: ${hw.cpuModel} (${hw.cpuCores} cores)`);
  if (hw.totalMemoryGb != null) {
    lines.push(`  RAM: ${hw.totalMemoryGb} GB total / ${hw.freeMemoryGb ?? '?'} GB free`);
  }
  if (hw.platform) lines.push(`  Platform: ${hw.platform} ${hw.arch ?? ''}`);
  lines.push(`  Engine: ${hw.ollamaHost}`);

  lines.push('', `Uptime: ${formatUptime(data.uptimeSeconds)} | ${data.timestamp}`);

  return lines.join('\n');
}
